import { Component } from '@theme/component';

/**
 * Zeequa countdown: ticks down to the ISO date/time in `data-end`.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} days
 * @property {HTMLElement} hours
 * @property {HTMLElement} minutes
 * @property {HTMLElement} seconds
 * @property {HTMLElement} [live] - The live part (hidden when expired).
 * @property {HTMLElement} [expired] - The expired message (shown when expired).
 *
 * @extends Component<Refs>
 */
class ZeequaCountdownComponent extends Component {
  requiredRefs = ['days', 'hours', 'minutes', 'seconds'];

  /** @type {number | undefined} */
  #timer;

  connectedCallback() {
    super.connectedCallback();
    this.#tick();
    this.#timer = window.setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#timer) window.clearInterval(this.#timer);
  }

  get #end() {
    const raw = this.dataset.end || '';
    const parsed = Date.parse(raw.trim().replace(' ', 'T'));
    return Number.isFinite(parsed) ? parsed : NaN;
  }

  #tick() {
    const { days, hours, minutes, seconds, live, expired } = this.refs;
    const end = this.#end;

    if (!Number.isFinite(end)) {
      this.#setExpired(false);
      return;
    }

    const diff = end - Date.now();

    if (diff <= 0) {
      this.#setExpired(true);
      if (this.#timer) window.clearInterval(this.#timer);
      return;
    }

    const pad = (/** @type {number} */ n) => String(n).padStart(2, '0');
    days.textContent = pad(Math.floor(diff / 864e5));
    hours.textContent = pad(Math.floor(diff / 36e5) % 24);
    minutes.textContent = pad(Math.floor(diff / 6e4) % 60);
    seconds.textContent = pad(Math.floor(diff / 1e3) % 60);

    if (live) live.hidden = false;
    if (expired) expired.hidden = true;
  }

  /** @param {boolean} isExpired */
  #setExpired(isExpired) {
    const { live, expired } = this.refs;
    if (live) live.hidden = isExpired;
    if (expired) expired.hidden = !isExpired;
    this.toggleAttribute('data-expired', isExpired);
  }
}

if (!customElements.get('zeequa-countdown-component')) {
  customElements.define('zeequa-countdown-component', ZeequaCountdownComponent);
}
